import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Plus, X, Mail, Smartphone, Monitor, ExternalLink, Pencil, RotateCcw, Languages, IdCard } from "lucide-react";
import { toast } from "sonner";
import IDCard from "@/components/IDCard";
import IDCardTemplateEditor from "@/components/IDCardTemplateEditor";
import { getEditorForSlug } from "@/components/CMSEditors";
import AttendancePanel from "@/components/AttendancePanel";
import EnquiriesPanel from "@/components/EnquiriesPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import BlogPanel from "@/components/BlogPanel";
import PermissionsPanel from "@/components/PermissionsPanel";
import PaymentCalendar from "@/components/PaymentCalendar";
import UserDrawer from "@/components/UserDrawer";
import AddUserModal from "@/components/AddUserModal";
import IDCardShortcutModal from "@/components/IDCardShortcutModal";
import PageBuilder from "@/components/pagebuilder/PageBuilder";
import SecurityPanel from "@/components/SecurityPanel";
import { EmailTestCard } from "@/components/EmailTestCard";
import { useT } from "@/context/LanguageContext";

export default function AdminDashboard({ isSuper = false }) {
  const { user } = useAuth();
  const { t } = useT();
  const [tab, setTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [pages, setPages] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserTab, setEditingUserTab] = useState("profile");
  const [addingUser, setAddingUser] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [idcardShortcutOpen, setIdcardShortcutOpen] = useState(false);

  const reload = async () => {
    const [u, p, s] = await Promise.all([
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/payments").catch(() => ({ data: [] })),
      api.get("/stats").catch(() => ({ data: null })),
    ]);
    setUsers(u.data); setPayments(p.data); setStats(s.data);
    const pg = await api.get("/cms/pages").catch(() => ({ data: [] }));
    setPages(pg.data);
  };

  useEffect(() => { reload(); }, []);

  // NOTE: Several tabs (Payments, Notify, Blog, CMS, Students role-view)
  // are temporarily hidden per operational request. The panels & routes
  // are still fully wired below so re-enabling is just uncommenting these
  // entries.
  const TABS = isSuper
    ? [
        { id: "overview", label: "Overview" },
        { id: "users", label: "Users" },
        // { id: "payments", label: "Payments" },
        { id: "attendance", label: "Attendance" },
        // { id: "notify", label: "Notify" },
        // { id: "blog", label: "Blog" },
        { id: "livepreview", label: "Live Preview" },
        { id: "enquiries", label: "Enquiries" },
        { id: "permissions", label: "Permissions" },
        { id: "security", label: "Security" },
      ]
    : [
        { id: "overview", label: "Overview" },
        { id: "students", label: "Students" },
        // { id: "payments", label: "Payments" },
        { id: "attendance", label: "Attendance" },
        // { id: "notify", label: "Notify" },
        // { id: "blog", label: "Blog" },
        { id: "enquiries", label: "Enquiries" },
        { id: "idcard", label: "ID Card" },
        { id: "security", label: "Security" },
      ];

  return (
    <DashboardLayout
      title={isSuper ? t("dash.super_admin_title") : t("dash.admin_title")}
      subtitle={isSuper ? t("dash.dojo_administration") : t("dash.student_administration")}
      onEditIDCards={() => setIdcardShortcutOpen(true)}
    >
      <div className="flex gap-2 mb-8 border-b border-[var(--dojo-border)] overflow-x-auto">
        {TABS.map((tabInfo) => (
          <button
            key={tabInfo.id}
            onClick={() => setTab(tabInfo.id)}
            data-testid={`tab-${tabInfo.id}`}
            className={`px-5 py-3 text-[11px] uppercase tracking-[0.2em] border-b-2 whitespace-nowrap transition-colors ${
              tab === tabInfo.id ? "border-[var(--dojo-green)] text-[var(--dojo-ink)]" : "border-transparent text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"
            }`}
          >
            {t(`tab.${tabInfo.id}`)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <Stat label={t("stat.students")} value={stats?.students ?? "—"} />
            {isSuper && <Stat label={t("stat.admins")} value={stats?.admins ?? "—"} />}
            <Stat label={t("stat.payments_due")} value={`$${(stats?.payments_due_total ?? 0).toFixed(2)}`} sub={`${stats?.payments_due_count ?? 0} ${t("stat.open")}`} />
            <Stat label={t("stat.active_members")} value={stats?.active_users ?? users.filter((u) => u.active).length} />
            <div className="col-span-2 border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-3">{t("stat.latest_payments")}</div>
              <div className="divide-y divide-[var(--dojo-border)]">
                {payments.slice(0, 5).map((p) => (
                  <div key={p.id} className="py-2 flex justify-between text-sm">
                    <span>{p.user_name} · {p.description}</span>
                    <span className="font-mono-accent">${p.amount.toFixed(2)}</span>
                  </div>
                ))}
                {payments.length === 0 && <div className="text-sm text-[var(--dojo-ink-soft)]">{t("stat.no_payments")}</div>}
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <IDCard user={user} />
          </div>
        </div>
      )}

      {tab === "security" && (
        <>
          {isSuper && <EmailTestCard defaultTo={user?.email || ""} />}
          <SecurityPanel isStaff />
        </>
      )}

      {(tab === "users" || tab === "students") && (
        <UsersPanel
          users={users}
          onEdit={setEditingUser}
          onReload={reload}
          onBill={(u) => setPayFor(u)}
          onAdd={() => setAddingUser(true)}
          isSuper={isSuper}
          onOpenTemplates={() => setTemplatesOpen(true)}
        />
      )}

      {tab === "payments" && (
        <PaymentsTabPanel
          payments={payments}
          users={users}
          onReload={reload}
          onNew={(u) => setPayFor(u)}
        />
      )}

      {tab === "attendance" && <AttendancePanel />}

      {tab === "enquiries" && <EnquiriesPanel />}

      {tab === "notify" && <NotificationsPanel />}

      {tab === "blog" && <BlogPanel />}

      {tab === "permissions" && isSuper && <PermissionsPanel />}

      {tab === "livepreview" && isSuper && (
        <LivePreviewPanel onReload={reload} />
      )}

      {tab === "idcard" && !isSuper && (() => {
        const idcardPage = pages.find((p) => p.slug === "idcard") || {
          slug: "idcard",
          title: "Member ID Card",
          content: {},
          updated_at: new Date().toISOString(),
        };
        return (
          <div className="space-y-4 max-w-3xl">
            <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Customize</div>
                  <h2 className="font-serif text-2xl">Member ID Card Design</h2>
                </div>
                <button onClick={() => setEditingPage(idcardPage)} className="btn-primary" data-testid="open-idcard-editor">Edit Global Design</button>
              </div>
              <p className="text-sm text-[var(--dojo-ink-soft)]">
                Edit the labels, kanji, accent color, dojo logo, and background image used on every member's certificate.
                Changes apply instantly to every student's dashboard.
              </p>
            </div>
            <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Per-template defaults</div>
                  <h3 className="font-serif text-xl">Edit Student / Team Class / Sensei templates</h3>
                </div>
                <button onClick={() => setTemplatesOpen(true)} className="btn-outline" data-testid="open-template-editor">Edit Templates</button>
              </div>
              <p className="text-sm text-[var(--dojo-ink-soft)]">
                Tweak the title, kanji, colors, and labels per template. Edits apply automatically to every user assigned that template — individual user overrides still take priority.
              </p>
            </div>
          </div>
        );
      })()}

      {editingUser && (
        <UserDrawer
          user={editingUser}
          currentUser={user}
          initialTab={editingUserTab}
          onClose={() => { setEditingUser(null); setEditingUserTab("profile"); }}
          onSaved={(updated) => { setEditingUser(updated); reload(); }}
        />
      )}
      {idcardShortcutOpen && (
        <IDCardShortcutModal
          onClose={() => setIdcardShortcutOpen(false)}
          onPick={(u) => {
            setIdcardShortcutOpen(false);
            setEditingUserTab("idcard");
            setEditingUser(u);
          }}
        />
      )}
      {addingUser && (
        <AddUserModal
          currentUser={user}
          onClose={() => setAddingUser(false)}
          onCreated={() => { setAddingUser(false); toast.success("User created"); reload(); }}
        />
      )}
      {payFor && (
        <NewPaymentModal user={payFor} onClose={() => setPayFor(null)} onSaved={reload} />
      )}
      {editingPage && (
        <EditPageModal page={editingPage} onClose={() => setEditingPage(null)} onSaved={() => { setEditingPage(null); reload(); }} />
      )}
      {templatesOpen && (
        <IDCardTemplateEditor onClose={() => setTemplatesOpen(false)} />
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-[var(--dojo-border)] p-6 bg-[var(--dojo-paper)]">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-2">{label}</div>
      <div className="font-serif text-4xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[var(--dojo-ink-soft)] mt-1">{sub}</div>}
    </div>
  );
}

function UsersPanel({ users, onEdit, onReload, onBill, onAdd, isSuper, onOpenTemplates }) {
  const { t } = useT();
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This removes their payments too.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]" data-testid="users-panel">
      <div className="px-6 py-4 border-b border-[var(--dojo-border)] flex flex-wrap justify-between items-center gap-3">
        <h2 className="font-serif text-2xl">{isSuper ? t("users.all_users") : t("tab.students")}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-[var(--dojo-ink-soft)]">{users.length} {t("users.records")}</span>
          {isSuper && onOpenTemplates && (
            <button onClick={onOpenTemplates} className="btn-outline flex items-center gap-2" data-testid="open-template-editor">
              <IdCard size={14} /> ID Card Templates
            </button>
          )}
          <button onClick={onAdd} className="btn-primary flex items-center gap-2" data-testid="add-user-btn">
            <Plus size={14} /> {t("users.add_user")}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--dojo-paper-alt)] text-[10px] uppercase tracking-[0.2em] text-[var(--dojo-ink-soft)]">
            <tr>
              <th className="text-left px-6 py-3">{t("users.col_name")}</th>
              <th className="text-left px-6 py-3">{t("users.col_email")}</th>
              <th className="text-left px-6 py-3">{t("users.col_role")}</th>
              <th className="text-left px-6 py-3">{t("users.col_belt")}</th>
              <th className="text-left px-6 py-3">{t("users.col_member_no")}</th>
              <th className="text-left px-6 py-3">{t("users.col_status")}</th>
              <th className="text-right px-6 py-3">{t("users.col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[var(--dojo-border)]" data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-3 font-medium">{u.name}</td>
                <td className="px-6 py-3 text-[var(--dojo-ink-soft)]">{u.email}</td>
                <td className="px-6 py-3 capitalize">{u.role.replace("_", " ")}</td>
                <td className="px-6 py-3">{u.belt_rank || "—"}</td>
                <td className="px-6 py-3 font-mono-accent text-xs">{u.member_number}</td>
                <td className="px-6 py-3">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                    u.active ? "border-[#2E4E3F] text-[#2E4E3F]" : "border-[var(--dojo-hinomaru)] text-[var(--dojo-hinomaru)]"
                  }`}>{u.active ? t("users.active") : t("users.inactive")}</span>
                </td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  <button className="text-xs underline mr-3" onClick={() => onEdit(u)} data-testid={`edit-user-${u.id}`}>{t("btn.edit")}</button>
                  {u.role === "student" && (
                    <button className="text-xs underline mr-3" onClick={() => onBill(u)} data-testid={`bill-user-${u.id}`}>{t("users.bill")}</button>
                  )}
                  {isSuper && (
                    <button className="text-xs text-[var(--dojo-hinomaru)] underline" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}>{t("btn.delete")}</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-[var(--dojo-ink-soft)]">{t("users.no_users")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentsTabPanel({ payments, users, onReload, onNew }) {
  const { t } = useT();
  const [showCalendar, setShowCalendar] = useState(() => {
    try { return localStorage.getItem("yk_payments_calendar_visible") !== "0"; }
    catch { return true; }
  });
  const toggleCalendar = () => {
    setShowCalendar((v) => {
      const next = !v;
      try { localStorage.setItem("yk_payments_calendar_visible", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="payments-tab">
      <PaymentsPanel payments={payments} onReload={onReload} users={users} onNew={onNew} />
      <div className="flex items-center justify-between border-t border-[var(--dojo-border)] pt-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">{t("payments.calendar")}</div>
        <button
          type="button"
          onClick={toggleCalendar}
          className="text-[11px] uppercase tracking-[0.2em] border border-[var(--dojo-border)] hover:border-[var(--dojo-ink)] px-3 py-1.5"
          data-testid="payments-calendar-toggle"
        >{showCalendar ? t("payments.hide_calendar") : t("payments.show_calendar")}</button>
      </div>
      {showCalendar && (
        <div data-testid="payments-calendar-wrapper">
          <PaymentCalendar payments={payments} />
        </div>
      )}
    </div>
  );
}

function PaymentsPanel({ payments, onReload, users, onNew }) {
  const { t } = useT();
  const [selectedUser, setSelectedUser] = useState("");

  const setStatus = async (p, status) => {
    try { await api.patch(`/payments/${p.id}`, { status }); toast.success("Updated"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (p) => {
    if (!window.confirm("Delete this payment record?")) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Deleted"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const sendReminder = async (p) => {
    try {
      const { data } = await api.post(`/payments/${p.id}/remind`);
      if (data.mode === "console") {
        toast.success(`Reminder logged (no SMTP configured) → ${data.to}`);
      } else {
        toast.success(`Reminder emailed to ${data.to}`);
      }
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const students = users.filter((u) => u.role === "student");

  return (
    <div className="space-y-6" data-testid="payments-panel">
      <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-6 flex gap-4 items-end">
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] block mb-2">{t("payments.bill_student")}</label>
          <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-3 py-2" data-testid="payment-user-select">
            <option value="">{t("payments.select_student")}</option>
            {students.map((u) => <option key={u.id} value={u.id}>{`${u.name} (${u.email})`}</option>)}
          </select>
        </div>
        <button
          className="btn-primary"
          disabled={!selectedUser}
          onClick={() => { const u = users.find((x) => x.id === selectedUser); if (u) onNew(u); }}
          data-testid="new-payment-btn"
        >{t("payments.new_invoice")}</button>
      </div>

      <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--dojo-paper-alt)] text-[10px] uppercase tracking-[0.2em] text-[var(--dojo-ink-soft)]">
              <tr>
                <th className="text-left px-6 py-3">{t("payments.col_student")}</th>
                <th className="text-left px-6 py-3">{t("payments.col_description")}</th>
                <th className="text-left px-6 py-3">{t("payments.col_amount")}</th>
                <th className="text-left px-6 py-3">{t("payments.col_due")}</th>
                <th className="text-left px-6 py-3">{t("payments.col_status")}</th>
                <th className="text-right px-6 py-3">{t("users.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-[var(--dojo-border)]" data-testid={`adm-payment-row-${p.id}`}>
                  <td className="px-6 py-3 font-medium">{p.user_name}</td>
                  <td className="px-6 py-3 text-[var(--dojo-ink-soft)]">{p.description}</td>
                  <td className="px-6 py-3 font-mono-accent">${p.amount.toFixed(2)}</td>
                  <td className="px-6 py-3 text-[var(--dojo-ink-soft)]">{p.due_date ? new Date(p.due_date).toLocaleDateString() : "—"}</td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                      p.status === "paid" ? "border-[#2E4E3F] text-[#2E4E3F]" :
                      p.status === "overdue" ? "border-[var(--dojo-hinomaru)] text-[var(--dojo-hinomaru)]" :
                      "border-[#B87F17] text-[#B87F17]"
                    }`}>{t(`payments.status_${p.status}`)}</span>
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {p.status !== "paid" && (
                      <button
                        className="text-xs underline mr-3 inline-flex items-center gap-1"
                        onClick={() => sendReminder(p)}
                        data-testid={`remind-payment-${p.id}`}
                        title={t("payments.remind_title")}
                      >
                        <Mail size={12} /> {t("payments.remind")}
                      </button>
                    )}
                    {p.status !== "paid" && <button className="text-xs underline mr-3" onClick={() => setStatus(p, "paid")} data-testid={`mark-paid-${p.id}`}>{t("payments.mark_paid")}</button>}
                    {p.status === "paid" && <button className="text-xs underline mr-3" onClick={() => setStatus(p, "due")} data-testid={`mark-due-${p.id}`}>{t("payments.reopen")}</button>}
                    {p.status === "due" && <button className="text-xs underline mr-3" onClick={() => setStatus(p, "overdue")} data-testid={`mark-overdue-${p.id}`}>{t("payments.status_overdue")}</button>}
                    <button className="text-xs text-[var(--dojo-hinomaru)] underline" onClick={() => del(p)} data-testid={`delete-payment-${p.id}`}><Trash2 size={12} className="inline" /></button>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={6} className="px-6 py-8 text-center text-[var(--dojo-ink-soft)]">{t("payments.no_payments")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PREVIEW_PAGES = [
  { slug: "home", label: "Home", path: "/" },
  { slug: "about", label: "About", path: "/about" },
  { slug: "programs", label: "Programs", path: "/programs" },
  { slug: "schedule", label: "Schedule", path: "/schedule" },
  { slug: "contact", label: "Contact", path: "/contact" },
];

function LivePreviewPanel({ onReload }) {
  const [slug, setSlug] = useState("home");
  const [device, setDevice] = useState("desktop");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statuses, setStatuses] = useState({}); // slug -> { published, draft, spanish }
  const [translating, setTranslating] = useState(false);

  const loadStatuses = async () => {
    try {
      const { data } = await api.get("/cms/pages");
      const map = {};
      (data || []).forEach((pg) => {
        const c = pg.content || {};
        map[pg.slug] = {
          published: Array.isArray(c.blocks) && c.blocks.length > 0,
          draft: Array.isArray(c.draft) && c.draft.length > 0,
          spanish: !!c.i18n?.es,
        };
      });
      setStatuses(map);
    } catch (e) {
      /* ignore */
    }
  };
  useEffect(() => { loadStatuses(); }, [refreshKey]);

  const current = PREVIEW_PAGES.find((p) => p.slug === slug) || PREVIEW_PAGES[0];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const src = `${origin}${current.path}`;

  const openEditor = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cms/pages/${slug}`);
      setEditing(data);
    } catch (e) {
      setEditing({ slug, title: current.label, content: {} });
    } finally {
      setLoading(false);
    }
  };

  const restoreOriginal = async () => {
    if (!window.confirm(`Restore the original ${current.label} design? The published blocks and any draft for this page are removed, and visitors see the original hand-built layout again.`)) return;
    setLoading(true);
    try {
      const { data: pg } = await api.get(`/cms/pages/${slug}`);
      const content = { ...(pg.content || {}) };
      delete content.blocks;
      delete content.draft;
      if (content.i18n?.es) {
        const es = { ...content.i18n.es };
        delete es.blocks;
        delete es.draft;
        content.i18n = { ...content.i18n, es };
      }
      await api.put(`/cms/pages/${slug}`, { title: pg.title || current.label, content });
      toast.success(`${current.label} restored to its original design`);
      setRefreshKey((k) => k + 1);
      if (onReload) onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const translate = async (slugs) => {
    setTranslating(true);
    try {
      for (const s of slugs) {
        const label = PREVIEW_PAGES.find((p) => p.slug === s)?.label || s;
        try {
          const { data } = await api.post(`/cms/pages/${s}/translate`);
          toast.success(`${label}: ${data.strings_translated} of ${data.strings_found} texts translated to Spanish`);
        } catch (e) {
          toast.error(`${label}: ${formatApiError(e)}`);
        }
      }
      await loadStatuses();
      setRefreshKey((k) => k + 1);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="livepreview-panel">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Live Preview</div>
          <h3 className="font-serif text-xl">Edit your public website</h3>
          <p className="text-xs text-[var(--dojo-ink-soft)] mt-1 max-w-xl">
            Pick a page, preview it live, then click <span className="font-medium">Edit this page</span> to add images, text and sections with drag-and-drop.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--dojo-border)] bg-[var(--dojo-paper-alt)] px-4 py-3 flex flex-wrap items-center gap-3" data-testid="translate-bar">
        <div className="flex items-center gap-2 text-xs">
          <Languages size={15} className="text-[var(--dojo-green-dark)]" />
          <span className="font-medium uppercase tracking-wider text-[10px] text-[var(--dojo-green-dark)]">Spanish site</span>
          <span className="text-[var(--dojo-ink-soft)]">
            {statuses[slug]?.spanish
              ? `${current.label} has a Spanish copy — switch the site language to review it, or fix wording in the editor's Español tab.`
              : `${current.label} has no Spanish copy yet. Translate it, then review the wording in the editor's Español tab.`}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => translate([slug])}
            disabled={translating}
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-[var(--dojo-maroon)] text-[var(--dojo-maroon)] text-xs uppercase tracking-wider"
            data-testid="translate-page-btn"
          >
            <Languages size={13} /> {translating ? "Translating…" : `Translate ${current.label}`}
          </button>
          <button
            onClick={() => translate(PREVIEW_PAGES.map((p) => p.slug))}
            disabled={translating}
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-[var(--dojo-border)] text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)] text-xs uppercase tracking-wider"
            data-testid="translate-all-btn"
          >
            All 5 pages
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--dojo-border)] bg-[var(--dojo-green-soft)] px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[var(--dojo-ink)]" data-testid="livepreview-help">
        <span className="font-medium uppercase tracking-wider text-[10px] text-[var(--dojo-green-dark)]">How it works</span>
        <span><span className="font-semibold">1.</span> Pick a page below</span>
        <span><span className="font-semibold">2.</span> Click <span className="font-semibold">Edit this page</span> — drag in sections, click text to type, upload photos</span>
        <span><span className="font-semibold">3.</span> <span className="font-semibold">Save draft</span> to keep working, then <span className="font-semibold">Publish</span> when it looks right</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--dojo-border)] pb-3">
        {PREVIEW_PAGES.map((p) => {
          const st = statuses[p.slug] || {};
          return (
          <button
            key={p.slug}
            onClick={() => setSlug(p.slug)}
            data-testid={`preview-page-${p.slug}`}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border transition-colors inline-flex items-center gap-1.5 ${
              slug === p.slug
                ? "border-[var(--dojo-maroon)] text-[var(--dojo-maroon)] bg-[var(--dojo-maroon-soft)]"
                : "border-[var(--dojo-border)] text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"
            }`}
          >
            {p.label}
            {st.draft && (
              <span className="normal-case tracking-normal text-[9px] px-1 rounded bg-[var(--dojo-green-soft)] text-[var(--dojo-green-dark)]">draft</span>
            )}
            {!st.draft && st.published && (
              <span className="normal-case tracking-normal text-[9px] px-1 rounded bg-[var(--dojo-maroon-soft)] text-[var(--dojo-maroon)]">live</span>
            )}
            {st.spanish && (
              <span className="normal-case tracking-normal text-[9px] px-1 rounded border border-[var(--dojo-border)] text-[var(--dojo-ink-soft)]">es</span>
            )}
          </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setDevice(device === "desktop" ? "mobile" : "desktop")}
            className="p-2 rounded border border-[var(--dojo-border)] text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"
            title="Toggle device size"
            data-testid="preview-device-toggle"
          >
            {device === "desktop" ? <Smartphone size={15} /> : <Monitor size={15} />}
          </button>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded border border-[var(--dojo-border)] text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"
            title="Open in new tab"
          >
            <ExternalLink size={15} />
          </a>
          {(statuses[slug]?.published || statuses[slug]?.draft) && (
            <button
              onClick={restoreOriginal}
              disabled={loading}
              className="inline-flex items-center gap-1 px-3 py-2 rounded border border-[var(--dojo-border)] text-xs uppercase tracking-wider text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]"
              data-testid="restore-original-design"
              title="Remove the block layout and show the original hand-built page again"
            >
              <RotateCcw size={14} /> Restore original
            </button>
          )}
          <button
            className="btn-primary inline-flex items-center gap-1"
            onClick={openEditor}
            disabled={loading}
            data-testid="edit-this-page"
          >
            <Pencil size={14} /> {loading ? "Loading…" : "Edit this page"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--dojo-border)] bg-[var(--dojo-paper-alt)] p-3 overflow-hidden">
        <div className={`mx-auto transition-all ${device === "mobile" ? "max-w-[390px]" : "max-w-full"}`}>
          <div className="text-[11px] text-[var(--dojo-ink-soft)] mb-2 font-mono-accent truncate">{src}</div>
          <iframe
            key={`${slug}-${refreshKey}`}
            title="live-preview"
            src={src}
            className="w-full rounded-lg bg-[var(--dojo-paper)] border border-[var(--dojo-border)]"
            style={{ height: "70vh" }}
          />
        </div>
      </div>

      {editing && (
        <PageBuilder
          slug={editing.slug}
          page={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setRefreshKey((k) => k + 1);
            if (onReload) onReload();
          }}
        />
      )}
    </div>
  );
}

function NewPaymentModal({ user, onClose, onSaved }) {
  const { t } = useT();
  const [desc, setDesc] = useState("Monthly Tuition");
  const [amount, setAmount] = useState("120");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payments", {
        user_id: user.id,
        description: desc,
        amount: Number(amount),
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        status: "due",
      });
      toast.success("Invoice created");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`${t("payments.new_invoice")} · ${user.name}`} onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <Field label={t("payments.col_description")}><input value={desc} onChange={(e) => setDesc(e.target.value)} className="input" data-testid="new-pay-desc" /></Field>
        <Field label={t("payments.col_amount")}><input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="input" data-testid="new-pay-amount" /></Field>
        <Field label={t("payments.col_due")}><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input" data-testid="new-pay-due" /></Field>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="btn-primary flex-1" disabled={busy} data-testid="new-pay-save">{busy ? t("btn.saving") : t("cms.create")}</button>
          <button type="button" className="btn-outline" onClick={onClose}>{t("btn.cancel")}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditPageModal({ page, onClose, onSaved }) {
  const { t } = useT();
  const [title, setTitle] = useState(page.title);
  const [content, setContent] = useState(page.content);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [, setShowJson] = useState(false);

  const Editor = getEditorForSlug(page.slug);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.put(`/cms/pages/${page.slug}`, { title, content });
      toast.success("Page saved");
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`${t("cms.edit_content")} /${page.slug}`} onClose={onClose} wide>
      <form onSubmit={save} className="space-y-5">
        {Editor ? (
          <Editor value={content} onChange={setContent} title={title} onTitleChange={setTitle} />
        ) : (
          <Field label={t("cms.content_json")}>
            <textarea
              value={JSON.stringify(content, null, 2)}
              onChange={(e) => {
                try { setContent(JSON.parse(e.target.value)); setErr(""); }
                catch (pe) { setErr("Invalid JSON: " + pe.message); }
              }}
              rows={18}
              spellCheck={false}
              className="input font-mono-accent text-xs"
              data-testid="edit-page-json"
            />
          </Field>
        )}

        {Editor && (
          <details className="border-t border-[var(--dojo-border)] pt-3">
            <summary className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] cursor-pointer" onClick={() => setShowJson((v) => !v)}>
              {t("cms.advanced_raw_json")}
            </summary>
            <pre className="text-xs font-mono-accent bg-[var(--dojo-paper-alt)] p-3 mt-2 overflow-auto max-h-60">{JSON.stringify(content, null, 2)}</pre>
          </details>
        )}

        {err && <div className="text-[var(--dojo-hinomaru)] text-sm" data-testid="edit-page-error">{err}</div>}
        <div className="flex gap-3 sticky bottom-0 bg-[var(--dojo-paper)] pt-3 border-t border-[var(--dojo-border)]">
          <button type="submit" className="btn-primary flex-1" disabled={busy} data-testid="edit-page-save">{busy ? t("btn.saving") : t("cms.save_page")}</button>
          <button type="button" className="btn-outline" onClick={onClose}>{t("btn.cancel")}</button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose} data-testid="modal-overlay">
      <div
        className={`bg-[var(--dojo-paper)] border border-[var(--dojo-border)] w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--dojo-border)]">
          <h3 className="font-serif text-2xl tracking-tight">{title}</h3>
          <button onClick={onClose} className="p-1 hover:text-[var(--dojo-hinomaru)]" data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
